/**
 * ChannelsChoice template.
 */
import Animator from '@components/elements/animator/animator';
import ChannelView from '@shared/types/enums/channelView';
import ChannelsChoiceList from '../../sections/channelChoice/channelsChoiceList';
import ChannelsChoiceDescription from '../../sections/channelChoice/channelsChoiceDescription';
import Focus from '@shared/types/enums/focus';
import Storage from '../../../../shared/utils/storage';
import goToChannelsClassic from '@shared/actions/channels/goToChannelsClassic';
import goToChannelsGrid from '@shared/actions/channels/goToChannelsGrid';
import goToChannelsLineup from '@shared/actions/channels/goToChannelsLineup';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { selectMenuCollapse } from '@shared/store/slices/menu.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useMemo } from 'react';
import { selectCurrentParams } from '@shared/store/slices/router.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';

/**
 * Displays different choice views available for the channels.
 */
export default function ChannelsChoiceTemplate() {
  const { props } = useChannelsChoice();
  return (
    <Animator {...props.animator}>
      <ChannelsChoiceDescription />
      <ChannelsChoiceList {...props.list} />
    </Animator>
  );
}

/**
 * Hooks for the channels choice template. Returns the props required for its children.
 */
function useChannelsChoice() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const currentParams = useAppSelector(selectCurrentParams);
  const { favouriteChannels } = useAppSelector(channelsSelector);
  const menuExpanded = !useAppSelector(selectMenuCollapse);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  /**
   * Handles the mouse enter event.
   */
  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.CHANNELS_CHOICE));
  }, []);

  /**
   * Selects a view based on the index.
   */
  const onSelectView = useCallback(
    (view: ChannelView) => {
      Storage.set('userChannelViewSelection', `${view}`);
      if (currentParams?.fromSettings) {
        dispatch(popRoute());
        return;
      }
      switch (view) {
        default:
        case ChannelView.Classic:
          goToChannelsClassic();
          break;
        case ChannelView.Lineup:
          goToChannelsLineup({
            channel: favouriteChannels?.[0],
            stacked: false,
          });
          break;
        case ChannelView.Grid:
          goToChannelsGrid();
          break;
      }
    },
    [favouriteChannels]
  );

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          if (currentParams?.fromSettings) {
            dispatch(popRoute());
          } else {
            dispatch(setFocus(Focus.MENU));
          }
          break;
        case 'RIGHT':
        case 'SELECT':
          dispatch(setFocus(Focus.CHANNELS_CHOICE));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      props: {
        animator: {
          collapse: { horizontal: menuExpanded },
          type: Animator.Type.CHANNELS_CHOICE,
          onMouseEnter,
        },
        list: {
          onSelectView,
        },
      },
    };
  }, [menuExpanded, onMouseEnter, onSelectView]);
}
